import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { FaCheckCircle, FaPlug, FaShieldAlt } from "react-icons/fa";
import "../product/AdminProducts.css";
import "../Couriers/CourierSettings.css";

/**
 * Settings → Payments: the store owner's own JazzCash and Easypaisa merchant
 * accounts. Keys are saved encrypted and never shown again. Customers pay on
 * the gateway's page; an order counts as paid only after the gateway confirms.
 */
export default function PaymentSettings() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  const apiBase = (axios.defaults.baseURL || window.location.origin).replace(/\/+$/, "");

  const load = () => {
    axios.get("/api/admin/payments").then((res) => {
      if (res.data.status !== 200) return;
      const s = res.data.settings;
      setSettings(s);
      setForm({
        hold_minutes: s.hold_minutes,
        jazzcash: { enabled: s.jazzcash.enabled, sandbox: s.jazzcash.sandbox, merchant_id: s.jazzcash.merchant_id, password: "", salt: "" },
        easypaisa: {
          enabled: s.easypaisa.enabled,
          sandbox: s.easypaisa.sandbox,
          store_id: s.easypaisa.store_id,
          account_num: s.easypaisa.account_num,
          username: s.easypaisa.username,
          password: "",
          hash_key: "",
        },
      });
    });
  };

  useEffect(() => {
    document.title = "Payments — Admin";
    load();
  }, []);

  const set = (code, key, value) => setForm((current) => ({ ...current, [code]: { ...current[code], [key]: value } }));

  const save = (quiet = false) => {
    setBusy("save");
    return axios.put("/api/admin/payments", form)
      .then((res) => {
        if (res.data.status === 200) {
          if (!quiet) swal("Saved", res.data.message, "success");
          load();
        }
        return res;
      })
      .catch((err) => {
        swal("Not saved", err.response?.data?.message || "Please check the fields.", "error");
        throw err;
      })
      .finally(() => setBusy(""));
  };

  const test = (code) => {
    setBusy(`test-${code}`);
    save(true)
      .then(() => axios.post(`/api/admin/payments/${code}/test`))
      .then((res) => swal(res.data.status === 200 ? "Connected" : "Not connected", res.data.message, res.data.status === 200 ? "success" : "error"))
      .catch(() => {})
      .finally(() => setBusy(""));
  };

  if (!form) {
    return <div className="ap"><div className="p-5 text-center"><div className="spinner-border text-primary" role="status" /></div></div>;
  }

  const saved = (flag) => (flag ? <span className="cs-saved"><FaCheckCircle size={10} /> saved</span> : null);
  const modeBadge = (sandbox) => (
    <span className={`badge ${sandbox ? "bg-warning text-dark" : "bg-success"}`}>{sandbox ? "Sandbox (test)" : "Live"}</span>
  );

  return (
    <div className="ap cs">
      <div className="ap-head">
        <div>
          <h4>Payments</h4>
          <p>Let customers pay online with JazzCash or Easypaisa, using your own merchant accounts. Money goes straight to your account.</p>
        </div>
      </div>

      <div className="cs-how">
        <div><span>1</span><strong>Get a merchant account</strong> from JazzCash or Easypaisa. Start with their <strong>sandbox</strong> (test) keys.</div>
        <div><span>2</span><strong>Paste the keys</strong> below, press <strong>Test connection</strong>, turn it <strong>On</strong>, then place one test order and pay it.</div>
        <div><span>3</span><strong>Go live:</strong> paste the live keys and switch Sandbox off. Customers now see "Pay now" at checkout.</div>
      </div>

      <div className="mo-health is-ok mb-3" style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 16px", borderRadius: 12, background: "#eff6ff", color: "#1e3a8a", fontSize: 13 }}>
        <FaShieldAlt style={{ marginTop: 3 }} />
        <div>
          Customers pay on JazzCash's or Easypaisa's own secure page. An order is marked paid only after the payment company confirms it and the amount matches.
          Unpaid orders are held for {form.hold_minutes} minutes, then cancelled and their stock goes back on sale. They cannot be confirmed or shipped until paid.
        </div>
      </div>

      <div className="cs-grid" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
        {/* JazzCash */}
        <section className="ap-card cs-card">
          <div className="cs-card-head">
            <span className="cs-logo is-leopards">JC</span>
            <div className="flex-grow-1">
              <h5>JazzCash {modeBadge(form.jazzcash.sandbox)}</h5>
              <small>Wallet, debit and credit cards, and vouchers. Keys are in the JazzCash merchant portal.</small>
            </div>
            <label className="cs-switch">
              <input type="checkbox" checked={!!form.jazzcash.enabled} onChange={(e) => set("jazzcash", "enabled", e.target.checked)} /> On
            </label>
          </div>
          <label className="form-label small fw-semibold">Merchant ID</label>
          <input className="form-control form-control-sm mb-2" value={form.jazzcash.merchant_id} onChange={(e) => set("jazzcash", "merchant_id", e.target.value)} placeholder="MC12345" />
          <div className="row g-2">
            <div className="col-6">
              <label className="form-label small fw-semibold">Password {saved(settings.jazzcash.has_password)}</label>
              <input type="password" autoComplete="off" className="form-control form-control-sm mb-2" value={form.jazzcash.password}
                placeholder={settings.jazzcash.has_password ? "Keep saved" : "Password"} onChange={(e) => set("jazzcash", "password", e.target.value)} />
            </div>
            <div className="col-6">
              <label className="form-label small fw-semibold">Integrity Salt {saved(settings.jazzcash.has_salt)}</label>
              <input type="password" autoComplete="off" className="form-control form-control-sm mb-2" value={form.jazzcash.salt}
                placeholder={settings.jazzcash.has_salt ? "Keep saved" : "Integrity salt"} onChange={(e) => set("jazzcash", "salt", e.target.value)} />
            </div>
          </div>
          <label className="d-flex align-items-center gap-2 small fw-semibold mb-2">
            <input type="checkbox" checked={!!form.jazzcash.sandbox} onChange={(e) => set("jazzcash", "sandbox", e.target.checked)} /> Sandbox (test payments, no real money)
          </label>
          <div className="small text-muted mb-2">
            In the JazzCash portal, set the <strong>Return URL</strong> to:<br />
            <code style={{ wordBreak: "break-all" }}>{apiBase}/api/payments/jazzcash/return</code>
          </div>
          <div className="cs-actions">
            <button type="button" className="ap-btn" onClick={() => test("jazzcash")} disabled={!!busy}>
              <FaPlug size={11} /> {busy === "test-jazzcash" ? "Testing…" : "Test connection"}
            </button>
          </div>
        </section>

        {/* Easypaisa */}
        <section className="ap-card cs-card">
          <div className="cs-card-head">
            <span className="cs-logo is-money">EP</span>
            <div className="flex-grow-1">
              <h5>Easypaisa {modeBadge(form.easypaisa.sandbox)}</h5>
              <small>Easypaisa account, debit and credit cards. Keys come from Easypaisa's merchant (Easypay) team.</small>
            </div>
            <label className="cs-switch">
              <input type="checkbox" checked={!!form.easypaisa.enabled} onChange={(e) => set("easypaisa", "enabled", e.target.checked)} /> On
            </label>
          </div>
          <div className="row g-2">
            <div className="col-6">
              <label className="form-label small fw-semibold">Store ID</label>
              <input className="form-control form-control-sm mb-2" value={form.easypaisa.store_id} onChange={(e) => set("easypaisa", "store_id", e.target.value)} />
            </div>
            <div className="col-6">
              <label className="form-label small fw-semibold">Merchant account number</label>
              <input className="form-control form-control-sm mb-2" value={form.easypaisa.account_num} onChange={(e) => set("easypaisa", "account_num", e.target.value)} />
            </div>
          </div>
          <label className="form-label small fw-semibold">Hash key {saved(settings.easypaisa.has_hash_key)}</label>
          <input type="password" autoComplete="off" className="form-control form-control-sm mb-2" value={form.easypaisa.hash_key}
            placeholder={settings.easypaisa.has_hash_key ? "Keep saved" : "16-character hash key"} onChange={(e) => set("easypaisa", "hash_key", e.target.value)} />
          <div className="row g-2">
            <div className="col-6">
              <label className="form-label small fw-semibold">API username</label>
              <input className="form-control form-control-sm mb-2" autoComplete="off" value={form.easypaisa.username} onChange={(e) => set("easypaisa", "username", e.target.value)} />
            </div>
            <div className="col-6">
              <label className="form-label small fw-semibold">API password {saved(settings.easypaisa.has_password)}</label>
              <input type="password" autoComplete="off" className="form-control form-control-sm mb-2" value={form.easypaisa.password}
                placeholder={settings.easypaisa.has_password ? "Keep saved" : "API password"} onChange={(e) => set("easypaisa", "password", e.target.value)} />
            </div>
          </div>
          <label className="d-flex align-items-center gap-2 small fw-semibold mb-2">
            <input type="checkbox" checked={!!form.easypaisa.sandbox} onChange={(e) => set("easypaisa", "sandbox", e.target.checked)} /> Sandbox (test payments, no real money)
          </label>
          <div className="small text-muted mb-2">
            Ask Easypaisa to allow these addresses on your store:<br />
            <code style={{ wordBreak: "break-all" }}>{apiBase}/api/payments/easypaisa/token</code><br />
            <code style={{ wordBreak: "break-all" }}>{apiBase}/api/payments/easypaisa/return</code>
          </div>
          <div className="cs-actions">
            <button type="button" className="ap-btn" onClick={() => test("easypaisa")} disabled={!!busy}>
              <FaPlug size={11} /> {busy === "test-easypaisa" ? "Testing…" : "Test connection"}
            </button>
          </div>
        </section>
      </div>

      <section className="ap-card cs-card mb-3">
        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label small fw-semibold">Hold unpaid online orders for (minutes)</label>
            <input type="number" min="10" max="1440" className="form-control form-control-sm" value={form.hold_minutes}
              onChange={(e) => setForm((c) => ({ ...c, hold_minutes: e.target.value }))} />
          </div>
          <div className="col-md-8 small text-muted">
            After this time an unpaid order is cancelled and its stock is released. 30 minutes suits most stores.
            Customers also need your <Link to="/policies/refund">refund</Link>, <Link to="/policies/privacy">privacy</Link> and{" "}
            <Link to="/policies/terms">terms</Link> pages: payment companies check for them. Edit them in Settings → Policies.
          </div>
        </div>
      </section>

      <div className="d-flex justify-content-end mb-4">
        <button type="button" className="ap-btn ap-btn-primary" onClick={() => save()} disabled={!!busy}>{busy === "save" ? "Saving…" : "Save payment settings"}</button>
      </div>
    </div>
  );
}
